import {generateClient} from "aws-amplify/api";
import {GraphQLQuery} from '@aws-amplify/api';
import {
    notifyError,
    notifySuccess
} from "../../../components/ServiceNotifications.tsx";
import {appSyncConfig} from "../config/stripe-api.ts";
import * as CustomersService from "../../customers/services/CustomersService.ts";

const client = generateClient({
                                  endpoint: appSyncConfig.endpoint,
                                  region: appSyncConfig.region,
                                  authMode: 'userPool'
                              });

export type StripeCustomer = {
    id: string;
    name?: string;
    description?: string;
    email?: string;
    phone?: string;
    delinquent?: boolean;
    created?: string;
}

export type CreateStripeCustomer = {
    createCustomer: StripeCustomer;
}

export type UpdateStripeCustomer = {
    updateCustomer: StripeCustomer;
}

export type DeleteStripeCustomer = {
    deleteCustomer: string;
}

export type GetStripeCustomer = {
    getCustomer: StripeCustomer;
}

const CREATE_STRIPE_CUSTOMER = `
    mutation createCustomer($input: CreateCustomerInput!) {
        createCustomer(input: $input) {
            id
            name
            description
            email
            phone
            delinquent
            created
        }
    }
`;

const UPDATE_STRIPE_CUSTOMER = `
    mutation updateCustomer($input: UpdateCustomerInput!) {
        updateCustomer(input: $input) {
            id
            name
            description
            email
            phone
            delinquent
            created
        }
    }
`;

const DELETE_STRIPE_CUSTOMER = `
    mutation deleteCustomer($input: DeleteCustomerInput!) {
        deleteCustomer(input: $input)
    }
`;

const GET_STRIPE_CUSTOMER = `
    query getCustomer($id: ID!) {
        getCustomer(id: $id) {
            id
            name
            description
            email
            phone
            delinquent
            created
        }
    }
`;

export const createStripeCustomer = async (localCustomerId: string, customerData: {
    name?: string;
    description?: string;
    email?: string;
    phone?: string;
}) => {
    let stripeCustomerId: string | null = null;
    
    try {
        const response = await client.graphql<GraphQLQuery<CreateStripeCustomer>>({
                                                                                      query: CREATE_STRIPE_CUSTOMER,
                                                                                      variables: {input: customerData},
                                                                                  });
        
        stripeCustomerId = response.data?.createCustomer?.id || null;
        
        if (stripeCustomerId) {
            const localCustomer = await CustomersService.fetch(localCustomerId);
            if (localCustomer) {
                try {
                    await CustomersService.update(localCustomerId, {
                        ...localCustomer,
                        billingInfo: {
                            ...localCustomer.billingInfo,
                            billingProviderRecordId: stripeCustomerId
                        }
                    });
                } catch (updateError) {
                    // Rollback: Delete the Stripe customer if local update fails
                    try {
                        await client.graphql<GraphQLQuery<DeleteStripeCustomer>>({
                            query: DELETE_STRIPE_CUSTOMER,
                            variables: {input: {id: stripeCustomerId}},
                        });
                    } catch (rollbackError) {
                        console.error('Failed to rollback Stripe customer creation:', rollbackError);
                    }
                    throw updateError;
                }
            }
        }
        
        notifySuccess('Stripe customer created successfully');
        return response.data?.createCustomer;
    } catch (e) {
        notifyError(e);
        throw e;
    }
};

export const updateStripeCustomer = async (stripeCustomerId: string, localCustomerId: string, customerData: {
    name?: string;
    description?: string;
    email?: string;
    phone?: string;
}) => {
    try {
        const response = await client.graphql<GraphQLQuery<UpdateStripeCustomer>>({
                                                                                      query: UPDATE_STRIPE_CUSTOMER,
                                                                                      variables: {input: {id: stripeCustomerId, ...customerData}},
                                                                                  });
        
        // Sync local customer with Stripe customer ID if missing
        const localCustomer = await CustomersService.fetch(localCustomerId);
        if (localCustomer && !localCustomer.billingInfo?.billingProviderRecordId) {
            await CustomersService.update(localCustomerId, {
                ...localCustomer,
                billingInfo: {
                    ...localCustomer.billingInfo,
                    billingProviderRecordId: stripeCustomerId
                }
            });
        }
        
        notifySuccess('Stripe customer updated successfully');
        return response.data?.updateCustomer;
    } catch (e) {
        notifyError(e);
        throw e;
    }
};

export const deleteStripeCustomer = async (stripeCustomerId: string, localCustomerId: string) => {
    // Update local record first to avoid orphaned Stripe customers
    const localCustomer = await CustomersService.fetch(localCustomerId);
    if (!localCustomer) {
        throw new Error('Local customer not found');
    }
    
    try {
        await CustomersService.update(localCustomerId, {
            ...localCustomer,
            billingInfo: {
                ...localCustomer.billingInfo,
                billingProviderRecordId: ''
            }
        });
        
        const response = await client.graphql<GraphQLQuery<DeleteStripeCustomer>>({
                                                                                      query: DELETE_STRIPE_CUSTOMER,
                                                                                      variables: {input: {id: stripeCustomerId}},
                                                                                  });
        
        notifySuccess('Stripe customer deleted successfully');
        return response.data?.deleteCustomer;
    } catch (e) {
        // Rollback: Restore the Stripe customer ID if Stripe deletion fails
        if (localCustomer) {
            try {
                await CustomersService.update(localCustomerId, {
                    ...localCustomer,
                    billingInfo: {
                        ...localCustomer.billingInfo,
                        billingProviderRecordId: stripeCustomerId
                    }
                });
            } catch (rollbackError) {
                console.error('Failed to rollback local customer update:', rollbackError);
            }
        }
        notifyError(e);
        throw e;
    }
};

export const getStripeCustomer = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<GetStripeCustomer>>({
                                                                                   query: GET_STRIPE_CUSTOMER,
                                                                                   variables: {id},
                                                                               });
        return response.data?.getCustomer;
    } catch (e) {
        notifyError(e);
        throw e;
    }
};